/** Kantenlänge einer Kachel in Pixeln. Wie im Original (AVAgent/TileImage). */
export const TILE_DIM = 32;

/**
 * Rand, der beim Packen einer Kachel in den Atlas mitkopiert wird.
 * Ersetzt den 42x42/-5px-Trick aus AVAgent.java: verhindert, dass
 * Kompressionsartefakte über die Kachelgrenze bluten. Der Empfänger
 * schneidet den Rand wieder weg.
 *
 * Der Rand kostet Fläche und damit Kodierzeit – bei 2 sind es 26 % Aufschlag
 * statt 56 % bei 4. Gemessen an einem Videobild mit q=0.1 sind die äußeren
 * Kachelpixel dabei nur 1,2-mal so fehlerhaft wie die Kachelmitte (bei Rand 4:
 * 1,1-mal). Ohne Rand springt das Verhältnis auf 1,7 – dann werden die
 * Kachelgrenzen als Säume sichtbar. Beim Text-Atlas ist der Rand ohne Wirkung.
 */
export const ATLAS_PADDING = 2;

/** Zellgröße im Atlas inklusive Rand ringsum. */
export const ATLAS_CELL = TILE_DIM + 2 * ATLAS_PADDING;

/**
 * Ab wie vielen unterschiedlichen Farbwerten eine Kachel als "Foto" gilt
 * und mit LOSSY_QUALITY kodiert wird. Darunter: Text/UI -> TEXT_QUALITY.
 * Schwellwert wie thresholdColor in AVAgent.java.
 */
export const COLOR_THRESHOLD = 150;

/** Qualität für den Foto-Atlas (0..1). */
export const LOSSY_QUALITY = 0.1;

/**
 * Qualität für zusammengefasste Deltas an langsame Viewer.
 * Muss unter LOSSY_QUALITY bleiben – sonst bekäme ein gedrosselter Viewer
 * ein besseres Bild als alle anderen.
 */
export const LOSSY_QUALITY_SLOW = 0.05;

/**
 * Qualität für den Text- und UI-Atlas (0..1).
 *
 * Dieser Kanal war verlustfrei: Nur bei exakt 1.0 schaltet WebP in den
 * verlustfreien Modus, jeder kleinere Wert ist verlustbehaftet. Mit dem hier
 * gesetzten Wert wird also auch Text komprimiert – die Kachelklassifikation
 * unterscheidet weiterhin, bestimmt jetzt aber nur noch die Stärke.
 *
 * Wo der Browser kein WebP kodieren kann, weicht der Sender auf PNG aus;
 * PNG kennt keine Qualitätsstufe und bleibt dort verlustfrei.
 */
export const TEXT_QUALITY = 0.9;

/**
 * Obergrenze für die längere Bildkante der Aufnahme.
 *
 * Ohne Deckel liefert getDisplayMedia die Quelle in ihrer eigenen Auflösung –
 * an einem 6K-Display sind das über 20 Megapixel, die jedes Bild durch
 * Auslesen, Vergleich und Kodierung müssen. Der Deckel greift nur nach unten:
 * Kleinere Quellen bleiben unangetastet, größere werden proportional
 * verkleinert. 2560 hält Text lesbar und kostet gegenüber 6K rund ein Fünftel
 * der Rechenzeit. Über ?maxedge=N im Sender lässt sich ohne Neubau vergleichen.
 */
export const MAX_CAPTURE_EDGE = 2560;

/**
 * Obergrenze der Bildkante, die der Server aus dem Netz annimmt.
 *
 * Geometrieangaben kommen aus `meta` und aus dem Keyframe-Header und legen ein
 * Canvas dieser Größe an – ungeprüft wäre das eine Einladung, Speicher zu
 * belegen. 8192 ist keine willkürliche Zahl: Bei 32er-Kacheln sind das 256
 * Kacheln je Kante, also genau die 65536 Kacheln, die sich mit den
 * 16-Bit-Kachelindizes des Protokolls noch adressieren lassen.
 */
export const MAX_FRAME_EDGE = 8192;

/**
 * Obergrenze der Bildrate – die einzige Stellschraube dafür.
 *
 * Der Sender startet mit diesem Wert; der Server drosselt bei Rückstau und
 * holt einen gedrosselten Sender höchstens bis hierher zurück. Die Quelle
 * selbst wird um SOURCE_RATE_FACTOR schneller angefordert (capture.ts), damit
 * die Ratenbegrenzung nicht auf jedes einzelne Bild angewiesen ist.
 */
export const MAX_FPS = 20;

/**
 * Wie viele Frames an Kachel-Historie der Server vorhält: eine Minute.
 * Bei 2560x1440 und 20 fps sind das ~540 kB je Raum (1200 Masken à 450 Byte).
 * An MAX_FPS gekoppelt, damit beim Anheben der Bildrate derselbe Zeitraum
 * abgedeckt bleibt.
 */
export const MAX_HISTORY = MAX_FPS * 60;

/**
 * Ab welchem Anteil geänderter Kacheln ein Keyframe kompakter ist
 * als ein Delta mit fast allen Kacheln.
 */
export const KEYFRAME_RATIO = 0.6;

/** Kachelklassen. Namen wie im Original: 'j' = JPEG-artig, 'g' = GIF-artig. */
export const CLASS_LOSSY = 0;
export const CLASS_LOSSLESS = 1;

/** Kachelanzahl in x-Richtung für eine gegebene Bildbreite. */
export function countTilesX(width: number): number {
  return Math.ceil(width / TILE_DIM);
}

/** Kachelanzahl in y-Richtung für eine gegebene Bildhöhe. */
export function countTilesY(height: number): number {
  return Math.ceil(height / TILE_DIM);
}

/** Takt der Lebenszeichen des Senders, solange er teilt. */
export const SENDER_ALIVE_INTERVAL = 1000;

/**
 * Ohne Frame oder Lebenszeichen gilt die Freigabe nach dieser Zeit als beendet.
 * Deutlich über SENDER_ALIVE_INTERVAL, damit ein einzelnes verzögertes
 * Lebenszeichen keinen Fehlalarm auslöst.
 */
export const SENDER_TIMEOUT = 4000;

/** Takt der WebSocket-Pings; wer bis zum nächsten Ping nicht antwortet, fliegt. */
export const HEARTBEAT_INTERVAL = 15000;
